/*
  <search-drawer>: the slide-out search (snippets/search-drawer.liquid).

  - Any [data-search-drawer-open] button opens it; the overlay, the close
    button and Escape close it, and focus returns to the button.
  - Focus is kept inside while it is open, and the page behind doesn't scroll.
  - Typing fetches sections/search-drawer-results through Shopify's
    predictive search, debounced, with stale requests aborted and answers
    cached, so fast typing never shows results for an older query.
  - Arrow keys move through suggestions and products; Enter follows the
    highlighted one, or searches for what was typed.
  - Searches are remembered (last 6, this browser only) and offered as
    "Recent searches" when the box is empty.
*/
if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends HTMLElement {
      DEBOUNCE = 180;
      RECENT_KEY = 'deserve:recent-searches';
      RECENT_MAX = 6;

      connectedCallback() {
        this.panel = this.querySelector('.search-drawer__panel');
        this.form = this.querySelector('[data-search-drawer-form]');
        this.input = this.querySelector('.search-drawer__input');
        this.clearButton = this.querySelector('[data-search-drawer-clear]');
        this.live = this.querySelector('[data-search-drawer-live]');
        this.empty = this.querySelector('[data-search-drawer-empty]');
        this.status = this.querySelector('[data-search-drawer-status]');
        this.recent = this.querySelector('[data-search-drawer-recent]');
        this.recentList = this.querySelector('[data-search-drawer-recent-list]');
        this.cache = new Map();
        this.activeIndex = -1;

        this.onDocumentClick = (event) => {
          const opener = event.target.closest('[data-search-drawer-open]');
          if (!opener) return;
          event.preventDefault();
          this.open(opener);
        };
        document.addEventListener('click', this.onDocumentClick);

        this.querySelectorAll('[data-search-drawer-close]').forEach((el) =>
          el.addEventListener('click', () => this.close())
        );

        this.input.addEventListener('input', () => this.onInput());
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.onInput();
          this.input.focus();
        });
        this.addEventListener('keydown', (event) => this.onKeydown(event));

        // Remember what people searched for when they pick a result.
        this.panel.addEventListener('click', (event) => {
          const option = event.target.closest('[data-search-option]');
          if (!option) return;
          if (option.hasAttribute('data-search-fill')) {
            // Trending / recent chips fill the box and show live results
            // rather than leaving the drawer.
            event.preventDefault();
            this.input.value = option.dataset.searchTerm || option.textContent.trim();
            this.onInput({ immediate: true });
            this.input.focus();
            return;
          }
          const typed = this.input.value.trim();
          if (typed) this.remember(typed);
        });

        this.querySelector('[data-search-drawer-recent-clear]')?.addEventListener('click', () => {
          this.writeRecent([]);
          this.renderRecent();
          this.input.focus();
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        this.unlockScroll();
      }

      /* ---- Open / close -------------------------------------------------- */

      open(opener) {
        if (this.isOpen) return;
        this.isOpen = true;
        this.opener = opener || document.activeElement;
        this.renderRecent();
        this.hidden = false;
        this.lockScroll();
        document.querySelectorAll('[data-search-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
        // Next frame, so the slide-in transition starts from closed.
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.input.focus({ preventScroll: true });
          this.input.select();
        });
      }

      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove('is-open');
        document.querySelectorAll('[data-search-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        this.unlockScroll();
        const done = () => {
          if (!this.isOpen) this.hidden = true;
        };
        this.panel.addEventListener('transitionend', done, { once: true });
        setTimeout(done, 450);
        this.opener?.focus({ preventScroll: true });
      }

      lockScroll() {
        // Pad by the scrollbar's width so the page doesn't jump sideways.
        const gap = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty('--search-drawer-scrollbar', `${gap}px`);
        document.body.classList.add('search-drawer-open');
      }

      unlockScroll() {
        document.body.classList.remove('search-drawer-open');
      }

      /* ---- Keyboard ------------------------------------------------------ */

      options() {
        const scope = this.live.hidden ? this.empty : this.live;
        return [...scope.querySelectorAll('[data-search-option]')].filter((el) => el.offsetParent);
      }

      setActive(index) {
        const options = this.options();
        options.forEach((el) => el.classList.remove('is-active'));
        if (!options.length) {
          this.activeIndex = -1;
          return;
        }
        this.activeIndex = (index + options.length) % options.length;
        const el = options[this.activeIndex];
        el.classList.add('is-active');
        el.scrollIntoView({ block: 'nearest' });
        if (!el.id) el.id = `SearchOption-${Math.random().toString(36).slice(2, 8)}`;
        this.input.setAttribute('aria-activedescendant', el.id);
      }

      clearActive() {
        this.activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');
        this.querySelectorAll('.is-active[data-search-option]').forEach((el) => el.classList.remove('is-active'));
      }

      onKeydown(event) {
        if (!this.isOpen) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key === 'Tab') {
          this.trapTab(event);
          return;
        }

        if (event.target !== this.input) return;

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          this.setActive(this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
        } else if (event.key === 'Enter' && this.activeIndex > -1) {
          const el = this.options()[this.activeIndex];
          if (el) {
            event.preventDefault();
            el.click();
          }
        }
      }

      trapTab(event) {
        const focusable = [
          ...this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])'),
        ].filter((el) => el.offsetParent && !el.hidden);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ---- Searching ----------------------------------------------------- */

      onInput({ immediate = false } = {}) {
        const terms = this.input.value.trim();
        this.clearButton.hidden = !this.input.value;
        this.clearActive();
        clearTimeout(this.timer);

        if (!terms) {
          this.abort?.abort();
          this.showEmpty();
          return;
        }
        this.timer = setTimeout(() => this.search(terms), immediate ? 0 : this.DEBOUNCE);
      }

      async search(terms) {
        const key = terms.toLowerCase();
        if (this.cache.has(key)) {
          this.showResults(this.cache.get(key), terms);
          return;
        }

        this.abort?.abort();
        this.abort = new AbortController();
        this.classList.add('is-loading');

        const params = new URLSearchParams({
          q: terms,
          'resources[type]': 'query,product,collection',
          'resources[limit]': '8',
          'resources[limit_scope]': 'each',
          section_id: 'search-drawer-results',
        });

        try {
          const response = await fetch(`${this.dataset.suggestUrl || '/search/suggest'}?${params}`, {
            signal: this.abort.signal,
          });
          if (!response.ok) throw new Error(response.status);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const markup = doc.querySelector('.search-drawer__results')?.outerHTML || '';
          this.cache.set(key, markup);
          // Only show it if it's still what's in the box.
          if (this.input.value.trim().toLowerCase() === key) this.showResults(markup, terms);
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.showResults(
            `<div class="search-drawer__none"><p class="search-drawer__none-heading">Search isn't responding</p><p class="search-drawer__none-text">Press Enter to see full results.</p></div>`,
            terms
          );
        } finally {
          this.classList.remove('is-loading');
        }
      }

      showResults(markup, terms) {
        this.live.innerHTML = markup;
        this.live.hidden = false;
        this.empty.hidden = true;
        this.input.setAttribute('aria-expanded', 'true');

        const results = this.live.querySelector('.search-drawer__results');
        const count = results ? Number(results.dataset.resultsCount || 0) : 0;
        const none = this.live.querySelector('[data-no-results]');
        this.status.textContent = none
          ? `No results for ${terms}`
          : `${count} ${count === 1 ? 'product' : 'products'} found for ${terms}`;

        // With nothing found, offer the empty state's ideas underneath.
        if (none) {
          this.empty.hidden = false;
        }
      }

      showEmpty() {
        this.live.hidden = true;
        this.live.innerHTML = '';
        this.empty.hidden = false;
        this.input.setAttribute('aria-expanded', 'false');
        this.status.textContent = '';
        this.renderRecent();
      }

      onSubmit(event) {
        const terms = this.input.value.trim();
        if (!terms) {
          event.preventDefault();
          this.input.focus();
          return;
        }
        if (this.activeIndex > -1) {
          event.preventDefault();
          this.options()[this.activeIndex]?.click();
          return;
        }
        this.remember(terms);
      }

      /* ---- Recent searches ----------------------------------------------- */

      readRecent() {
        try {
          const list = JSON.parse(window.localStorage.getItem(this.RECENT_KEY) || '[]');
          return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
        } catch (e) {
          return [];
        }
      }

      writeRecent(list) {
        try {
          window.localStorage.setItem(this.RECENT_KEY, JSON.stringify(list));
        } catch (e) {}
      }

      remember(terms) {
        const clean = terms.slice(0, 60);
        const list = this.readRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase());
        list.unshift(clean);
        this.writeRecent(list.slice(0, this.RECENT_MAX));
      }

      renderRecent() {
        if (!this.recent) return;
        const list = this.readRecent();
        this.recentList.replaceChildren(
          ...list.map((term) => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.className = 'search-drawer__chip search-drawer__chip--recent';
            a.href = `${this.dataset.searchUrl || '/search'}?q=${encodeURIComponent(term)}`;
            a.textContent = term;
            a.dataset.searchOption = '';
            a.dataset.searchTerm = term;
            a.dataset.searchFill = '';
            li.append(a);
            return li;
          })
        );
        this.recent.hidden = list.length === 0;
      }
    }
  );
}
